/**
 * Actions page (G5.6) — visible outcomes for governed actions.
 *
 * An approved proposal's story used to end at the inbox; this page shows
 * what actually happened: execution status, errors, the execution log, and
 * rollback for reversible succeeded actions. Reversibility is rendered
 * honestly — only the backend's can_rollback offers the button, and
 * stop-forward semantics come back in the rollback response message.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Modal,
  Page,
  Spinner,
  Text,
} from '@shopify/polaris';

import {
  fetchActions,
  fetchExecutionLogs,
  rollbackAction,
  type ActionSummary,
  type ExecutionLogEntry,
} from '../services/actionsApi';

const STATUS_TONE: Record<
  string,
  'success' | 'critical' | 'attention' | 'info' | undefined
> = {
  succeeded: 'success',
  failed: 'critical',
  executing: 'info',
  queued: 'info',
  approved: 'info',
  pending_approval: 'attention',
  rolled_back: 'attention',
  rollback_failed: 'critical',
  partially_executed: 'attention',
};

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function Actions() {
  const [actions, setActions] = useState<ActionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [detail, setDetail] = useState<ActionSummary | null>(null);
  const [logs, setLogs] = useState<ExecutionLogEntry[] | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchActions(50);
      setActions(data.actions);
      setError(null);
    } catch (err) {
      setError(
        (err as Error & { detail?: string }).detail ||
          'Could not load actions. Please try again.'
      );
      setActions([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (action: ActionSummary) => {
    setDetail(action);
    setLogs(null);
    setLogsError(null);
    try {
      const data = await fetchExecutionLogs(action.action_id);
      setLogs(data.entries);
    } catch (err) {
      // A 403 is an authorization outcome, not an empty audit trail —
      // report it as such instead of "no log entries".
      if ((err as Error & { status?: number }).status === 403) {
        setLogsError(
          'Viewing the execution log requires audit permission.'
        );
      } else {
        setLogsError('Could not load the execution log.');
      }
      setLogs([]);
    }
  };

  const handleRollback = async (action: ActionSummary) => {
    setRollingBack(true);
    setNotice(null);
    try {
      const result = await rollbackAction(action.action_id);
      setNotice(result.message);
      setDetail(null);
      await load();
    } catch (err) {
      setError(
        (err as Error & { detail?: string }).detail ||
          'Rollback failed. See the execution log for details.'
      );
    } finally {
      setRollingBack(false);
    }
  };

  const rows = (actions ?? []).map(action => [
    formatTimestamp(action.created_at),
    action.action_type,
    action.platform,
    <Badge key={`status-${action.action_id}`} tone={STATUS_TONE[action.status]}>
      {action.status}
    </Badge>,
    action.error_message || '—',
    <InlineStack key={`ops-${action.action_id}`} gap="200">
      <Button variant="plain" onClick={() => openDetail(action)}>
        Details
      </Button>
      {action.can_rollback && (
        <Button
          variant="plain"
          tone="critical"
          onClick={() => handleRollback(action)}
          loading={rollingBack}
        >
          Roll back
        </Button>
      )}
    </InlineStack>,
  ]);

  return (
    <Page
      title="Actions"
      subtitle="What your agents' approved actions actually did"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}
            {notice && (
              <Banner tone="success" onDismiss={() => setNotice(null)}>
                {notice}
              </Banner>
            )}

            {actions === null && (
              <InlineStack align="center">
                <Spinner size="large" accessibilityLabel="Loading actions" />
              </InlineStack>
            )}

            {actions !== null && actions.length === 0 && !error && (
              <Card>
                <Text as="p" tone="subdued">
                  No executed actions yet. Approved proposals appear here once
                  they run.
                </Text>
              </Card>
            )}

            {actions !== null && actions.length > 0 && (
              <Card>
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Created',
                    'Type',
                    'Platform',
                    'Status',
                    'Error',
                    '',
                  ]}
                  rows={rows}
                />
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.action_type} — ${detail.status}` : ''}
      >
        <Modal.Section>
          {detail && (
            <BlockStack gap="300">
              <Text as="p" variant="bodySm" tone="subdued">
                Target: {detail.target.entity_type} {detail.target.entity_id}
                {detail.target.entity_name
                  ? ` (${detail.target.entity_name})`
                  : ''}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Executed: {formatTimestamp(detail.execution_completed_at)}
                {detail.rollback_executed_at
                  ? ` · Rolled back: ${formatTimestamp(
                      detail.rollback_executed_at
                    )}`
                  : ''}
              </Text>
              {detail.error_message && (
                <Banner tone="critical">
                  {detail.error_message}
                  {detail.error_code ? ` (${detail.error_code})` : ''}
                </Banner>
              )}
              <Text as="h3" variant="headingSm">
                Execution log
              </Text>
              {logs === null && (
                <InlineStack align="center">
                  <Spinner size="small" accessibilityLabel="Loading log" />
                </InlineStack>
              )}
              {logsError !== null && (
                <Text as="p" tone="subdued" variant="bodySm">
                  {logsError}
                </Text>
              )}
              {logsError === null && logs !== null && logs.length === 0 && (
                <Text as="p" tone="subdued" variant="bodySm">
                  No log entries available.
                </Text>
              )}
              {logs !== null &&
                logs.map(entry => (
                  <InlineStack key={entry.id} gap="200" blockAlign="center">
                    <Badge>{entry.event_type}</Badge>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {formatTimestamp(entry.event_timestamp)}
                      {entry.http_status_code
                        ? ` · HTTP ${entry.http_status_code}`
                        : ''}
                      {entry.triggered_by ? ` · ${entry.triggered_by}` : ''}
                    </Text>
                  </InlineStack>
                ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
